/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Children,
  type JSXElementConstructor,
  isValidElement,
  type ReactElement,
  type ReactNode,
  Fragment,
  type ReactPortal,
} from "react";

import type {
  Builder,
  CompositionComponent,
  CompositionTupleOf,
} from "./builder";
import { type BaseMultipleBuilder, MultipleBuilder } from "./multiple";
import {
  type BaseSingleBuilder,
  type DefaultedSingleBuilder,
  type RequiredSingleBuilder,
  SingleBuilder,
} from "./single";

type InferProps<T> = T extends JSXElementConstructor<infer P> ? P : never;

type MapChildrenOptions = {
  [k: string]: Builder<
    readonly [CompositionComponent, ...CompositionComponent[]],
    any
  >;
};

type SpreadTupleToElement<
  T extends readonly [CompositionComponent, ...CompositionComponent[]],
> = T extends readonly [infer First extends CompositionComponent, ...infer Rest]
  ? Rest extends [CompositionComponent, ...CompositionComponent[]]
    ? [ReactElement<InferProps<First>, First>, ...SpreadTupleToElement<Rest>]
    : [ReactElement<InferProps<First>, First>]
  : never;

type MapChildrenResult<T extends MapChildrenOptions> = {
  [K in keyof T]: T[K] extends BaseSingleBuilder<infer C>
    ? T[K] extends
        RequiredSingleBuilder<infer C> | DefaultedSingleBuilder<infer C>
      ? SpreadTupleToElement<C>[number]
      : SpreadTupleToElement<C>[number] | null
    : T[K] extends BaseMultipleBuilder<infer C>
      ? SpreadTupleToElement<C>[number][]
      : never;
} & {
  rest: Array<ReactNode>;
};

function isValidComponentElement<T>(
  child: ReactNode,
  component: JSXElementConstructor<T> | string,
): child is ReactElement<T, typeof component> {
  return isValidElement(child) && child.type === component;
}

export const composition = {
  has<T>(
    children: ReactNode,
    component:
      | JSXElementConstructor<T>
      | string
      | readonly (JSXElementConstructor<T> | string)[],
    ignore: (
      child: ReactElement<T, JSXElementConstructor<T> | string> | ReactPortal,
    ) => boolean = () => false,
  ): boolean {
    return Children.toArray(children).some((child) => {
      if (!isValidElement(child)) {
        return false;
      }

      const components = [component].flat();

      for (const component of components) {
        if (!isValidComponentElement(child, component)) {
          continue;
        }
        return !ignore(child);
      }

      return false;
    });
  },
  single<
    const C extends
      | CompositionComponent
      | readonly [CompositionComponent, ...CompositionComponent[]],
  >(components: C): SingleBuilder<CompositionTupleOf<C>> {
    return new SingleBuilder(SingleBuilder.ensureComponentTuple(components));
  },
  multiple<
    const C extends
      | CompositionComponent
      | readonly [CompositionComponent, ...CompositionComponent[]],
  >(components: C): MultipleBuilder<CompositionTupleOf<C>> {
    return new MultipleBuilder(
      MultipleBuilder.ensureComponentTuple(components),
    );
  },
  split<T extends MapChildrenOptions>(
    children: ReactNode,
    options: T,
  ): MapChildrenResult<T> {
    const composition = Children.toArray(children)
      .map((child) => {
        if (isValidComponentElement(child, Fragment)) {
          return child.props.children;
        }
        return child;
      })
      .flat()
      .reduce(
        (acc: MapChildrenResult<T>, child) => {
          if (!isValidElement(child)) {
            acc.rest.push(child);
            return acc;
          }

          const found = Object.entries(options).find(([, builder]) => {
            return builder.match(child);
          });

          if (!found) {
            acc.rest.push(child);
            return acc;
          }

          const [key, builder] = found;
          if (builder.type === "single") {
            if (acc[key]) {
              throw new Error(
                `Expected single ${key} but found multiple ${key}`,
              );
            }
            Object.assign(acc, { [key]: child });
            return acc;
          }

          const list = acc[key] ?? [];
          if (!Array.isArray(list)) {
            throw new Error(`Expected multiple ${key} but found single ${key}`);
          }

          Object.assign(acc, { [key]: [...list, child] });
          return acc;
        },
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        { rest: [] } as MapChildrenResult<T>,
      );

    for (const [key, config] of Object.entries(options)) {
      if (composition[key] === undefined) {
        Object.assign(composition, { [key]: config.getDefault() });
      }
      if (composition[key]) {
        continue;
      }
      switch (config.type) {
        case "single": {
          config.assertRequired(key);
          Object.assign(composition, { [key]: config.getDefault() ?? null });
          break;
        }
        case "multiple": {
          config.assertRequired(key);
          Object.assign(composition, { [key]: config.getDefault() ?? [] });
          break;
        }
      }
    }
    return composition;
  },
};

export class CompositionError extends Error {
  constructor(component: string, parent: string) {
    super(`[react-ui] ${component} must be used within a ${parent} component`);
    this.name = "CompositionError";
  }
}

export function raiseCompositionError(
  component: string,
  parent: string,
): never {
  throw new CompositionError(component, parent);
}
